import { useCallback, useEffect, useMemo, useRef, useState } from "react";

/** Matches `PADDING_LEFT` in `TableContentCell` / `TableSkeleton`. */
export const TABLE_CELL_HORIZONTAL_PADDING = 8;
export const DEFAULT_TABLE_COLUMN_WIDTH = 160;
export const DEFAULT_FIRST_TABLE_COLUMN_WIDTH = 320;

export function getDefaultTableColumnWidth(
  isFirstColumn: boolean,
  width?: number,
  minWidth?: number,
): number {
  if (!isFirstColumn) {
    return width ?? minWidth ?? DEFAULT_TABLE_COLUMN_WIDTH;
  }

  if (width == null) {
    return Math.max(DEFAULT_FIRST_TABLE_COLUMN_WIDTH, minWidth ?? 0);
  }

  return Math.max(
    Math.min(width, DEFAULT_FIRST_TABLE_COLUMN_WIDTH),
    minWidth ?? 0,
  );
}

export function getTableColumnOuterWidth(innerWidth: number): number {
  return innerWidth + TABLE_CELL_HORIZONTAL_PADDING;
}

type PersistedWidths = Partial<Record<string, number>>;

function readPersisted(storageKey: string | undefined): PersistedWidths {
  if (!storageKey || typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return {};
    return parsed as PersistedWidths;
  } catch {
    return {};
  }
}

function mergeWidths<K extends string>(
  allColumns: readonly K[],
  defaults: Record<K, number>,
  mins: Partial<Record<K, number>>,
  persisted: PersistedWidths,
): Record<K, number> {
  const out = {} as Record<K, number>;
  for (const col of allColumns) {
    const d = defaults[col] ?? 150;
    const floor = mins[col] ?? 80;
    const p = persisted[col as string];
    out[col] =
      typeof p === "number" && Number.isFinite(p) && p >= floor ? p : d;
  }
  return out;
}

/**
 * Per-column inner widths (same units as `TableContentCell` `width` prop).
 *
 * When `persistenceKey` is set, widths are persisted to localStorage keyed by
 * `table-column-widths:<persistenceKey>:v3`, and cross-tab updates are synced
 * via the `storage` event. Resizing writes the final width atomically on
 * pointer-up; there is no intermediate "drag override" React state (live drag
 * visuals are handled by direct DOM manipulation in `table.tsx`).
 */
export function useColumnWidths<K extends string>(
  persistenceKey: string | undefined,
  allColumns: readonly K[],
  defaults: Record<K, number>,
  mins: Partial<Record<K, number>>,
): {
  widths: Record<K, number>;
  commitWidth: (col: K, width: number) => void;
} {
  const storageKey = persistenceKey
    ? `table-column-widths:${persistenceKey}:v3`
    : undefined;

  const [persisted, setPersisted] = useState<PersistedWidths>(() =>
    readPersisted(storageKey),
  );

  // Reload when the storage key changes (e.g. persistenceKey bump to reset).
  const lastStorageKeyRef = useRef<string | undefined>(storageKey);
  useEffect(() => {
    if (lastStorageKeyRef.current !== storageKey) {
      lastStorageKeyRef.current = storageKey;
      setPersisted(readPersisted(storageKey));
    }
  }, [storageKey]);

  // Sync across tabs/windows so resizing in one window reflects in others.
  useEffect(() => {
    if (!storageKey) return;
    const onStorage = (e: StorageEvent) => {
      if (e.key !== storageKey) return;
      if (e.storageArea && e.storageArea !== window.localStorage) return;
      try {
        setPersisted(e.newValue ? JSON.parse(e.newValue) ?? {} : {});
      } catch {
        setPersisted({});
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [storageKey]);

  // Session-only fallback when no persistenceKey is provided.
  const [sessionWidths, setSessionWidths] = useState<PersistedWidths>({});

  const widths = useMemo(
    () =>
      mergeWidths(
        allColumns,
        defaults,
        mins,
        storageKey ? persisted : sessionWidths,
      ),
    [allColumns, defaults, mins, persisted, sessionWidths, storageKey],
  );

  const commitWidth = useCallback(
    (col: K, width: number) => {
      const floor = mins[col] ?? 60;
      const clamped = Math.max(floor, width);
      if (!storageKey) {
        setSessionWidths((prev) => ({ ...prev, [col as string]: clamped }));
        return;
      }
      // Read–modify–write to localStorage synchronously. This is the source of
      // truth; React state is just a mirror for rendering. Writing outside of
      // a setState updater guarantees the write happens exactly once per call
      // (setState updaters can run twice under StrictMode).
      const current = readPersisted(storageKey);
      const next: PersistedWidths = { ...current, [col as string]: clamped };
      try {
        window.localStorage.setItem(storageKey, JSON.stringify(next));
      } catch {
        // ignore quota / privacy-mode errors; widths stay in-memory this session
      }
      setPersisted(next);
    },
    [mins, storageKey],
  );

  return { widths, commitWidth };
}
